import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import { useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import { User, Calendar, Clock, X, ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import './Checkout.css';

const Checkout = () => {
    const { cartItems, cartTotal, clearCart } = useCart();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const [currentUser, setCurrentUser] = useState(pb.authStore.model);
    const [loading, setLoading] = useState(false);
    const [showPicker, setShowPicker] = useState(false);
    const [showEmailErrorModal, setShowEmailErrorModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [formData, setFormData] = useState({
        name: currentUser?.name || '',
        email: currentUser?.email || '',
        phone: currentUser?.phone || '',
        date: '',
        time: '',
        notes: '',
        createAccount: true,
        updatePhoneInProfile: !!currentUser && !currentUser.phone,
        password: '',
        passwordConfirm: ''
    });

    useEffect(() => {
        if (cartItems.length === 0 && !showSuccessModal) {
            navigate('/gateaux');
        }

        const unsubscribe = pb.authStore.onChange((token, model) => {
            setCurrentUser(model);
            if (model) {
                setFormData(prev => ({
                    ...prev,
                    name: model.name || '',
                    email: model.email || '',
                    phone: model.phone || prev.phone,
                    updatePhoneInProfile: !model.phone
                }));
            }
        });

        return () => unsubscribe();
    }, [cartItems, navigate]);

    useEffect(() => {
        if ((showPicker && isMobile) || showEmailErrorModal || showSuccessModal) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }
        return () => { document.body.style.overflow = 'auto'; };
    }, [showPicker, isMobile, showEmailErrorModal, showSuccessModal]);

    const handleChange = (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setFormData({ ...formData, [e.target.name]: value });
    };

    // --- DOCTOLIB LOGIC ---
    const dayNames = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

    const isDateClosed = (date) => {
        if (!settings.openingHours) return false;

        // Check vacation
        if (settings.vacationStart && settings.vacationEnd) {
            const d = new Date(date);
            d.setHours(0, 0, 0, 0);
            const start = new Date(settings.vacationStart);
            const end = new Date(settings.vacationEnd);
            if (d >= start && d <= end) return true;
        }

        const dayName = dayNames[date.getDay()];
        return !settings.openingHours[dayName]?.ouvert;
    };

    const generateTimeSlots = (date) => {
        if (!settings.openingHours) return [];
        const dayName = dayNames[date.getDay()];
        const hours = settings.openingHours[dayName];
        if (!hours?.ouvert) return [];

        const slots = [];
        const parseRange = (range) => {
            if (!range) return [];
            const [start, end] = range.split(' - ');
            const [startH, startM] = start.split(':').map(Number);
            const [endH, endM] = end.split(':').map(Number);

            const curr = new Date(date);
            curr.setHours(startH, startM, 0, 0);

            const finish = new Date(date);
            finish.setHours(endH, endM, 0, 0);

            const r = [];
            while (curr < finish) {
                r.push(curr.toTimeString().substring(0, 5));
                curr.setMinutes(curr.getMinutes() + 30);
            }
            return r;
        };

        const allSlots = [...parseRange(hours.matin), ...parseRange(hours.apresMidi)];

        // Filter out past slots if today
        const now = new Date();
        if (date.toDateString() === now.toDateString()) {
            // Buffer of 2 hours for today
            const buffer = new Date(now.getTime() + 2 * 60 * 60 * 1000);
            return allSlots.filter(s => {
                const [h, m] = s.split(':').map(Number);
                const slotDate = new Date(date);
                slotDate.setHours(h, m, 0, 0);
                return slotDate > buffer;
            });
        }

        return allSlots;
    };

    const nextAvailable = useMemo(() => {
        const d = new Date();
        // Check next 14 days
        for (let i = 0; i < 14; i++) {
            const checkDate = new Date();
            checkDate.setDate(d.getDate() + i);
            if (isDateClosed(checkDate)) continue;
            const slots = generateTimeSlots(checkDate);
            if (slots.length > 0) {
                return {
                    date: checkDate,
                    dateStr: checkDate.toISOString().split('T')[0],
                    time: slots[0]
                };
            }
        }
        return null;
    }, [settings]);

    useEffect(() => {
        if (nextAvailable && !formData.date) {
            setFormData(prev => ({
                ...prev,
                date: nextAvailable.dateStr,
                time: nextAvailable.time
            }));
        }
    }, [nextAvailable]);

    const formatDateLong = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    const [modalDate, setModalDate] = useState(new Date());
    const calendarDays = useMemo(() => {
        const days = [];
        const start = new Date(modalDate);
        start.setDate(1);
        const startDay = start.getDay(); // 0 is sunday, 1 is monday

        // Add empty days for matching weekday
        const offset = startDay === 0 ? 6 : startDay - 1; // Start with Monday
        for (let i = 0; i < offset; i++) days.push(null);

        const lastDay = new Date(modalDate.getFullYear(), modalDate.getMonth() + 1, 0).getDate();
        for (let i = 1; i <= lastDay; i++) {
            const d = new Date(modalDate.getFullYear(), modalDate.getMonth(), i);
            days.push(d);
        }
        return days;
    }, [modalDate]);

    const handleSelectDay = (d) => {
        if (!d || isDateClosed(d) || d < new Date().setHours(0, 0, 0, 0)) return;
        const slots = generateTimeSlots(d);
        if (slots.length > 0) {
            setFormData(prev => ({
                ...prev,
                date: d.toISOString().split('T')[0],
                time: slots[0]
            }));
        }
    };

    const renderPickerContent = () => (
        <div className="picker-content-wrapper">
            <div className="calendar-section">
                <div className="calendar-nav">
                    <button type="button" onClick={() => setModalDate(new Date(modalDate.setMonth(modalDate.getMonth() - 1)))}><ChevronLeft size={20} /></button>
                    <span>{modalDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</span>
                    <button type="button" onClick={() => setModalDate(new Date(modalDate.setMonth(modalDate.getMonth() + 1)))}><ChevronRight size={20} /></button>
                </div>
                <div className="calendar-grid">
                    {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => <div key={d} className="calendar-weekday">{d}</div>)}
                    {calendarDays.map((d, i) => {
                        const isClosed = d && isDateClosed(d);
                        const isPast = d && d < new Date().setHours(0, 0, 0, 0);
                        const isSelected = d && d.toISOString().split('T')[0] === formData.date;
                        return (
                            <div
                                key={i}
                                className={`calendar-day ${!d ? 'empty' : ''} ${isClosed || isPast ? 'disabled' : ''} ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSelectDay(d)}
                            >
                                {d ? d.getDate() : ''}
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="time-slots-section">
                <h4>Heures disponibles</h4>
                <div className="slots-grid">
                    {generateTimeSlots(new Date(formData.date || nextAvailable?.dateStr)).length > 0 ? (
                        generateTimeSlots(new Date(formData.date || nextAvailable?.dateStr)).map(slot => (
                            <button
                                key={slot}
                                type="button"
                                className={`slot-btn ${formData.time === slot ? 'active' : ''}`}
                                onClick={() => {
                                    setFormData(prev => ({ ...prev, time: slot }));
                                    setShowPicker(false);
                                }}
                            >
                                {slot}
                            </button>
                        ))
                    ) : (
                        <p style={{ gridColumn: 'span 3', color: '#999', fontSize: '0.9rem' }}>Aucun créneau disponible pour ce jour.</p>
                    )}
                </div>
            </div>
        </div>
    );
    // ----------------------

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            if (!formData.date || !formData.time) {
                alert("Veuillez choisir une date et une heure de retrait.");
                setLoading(false);
                return;
            }

            // --- Real-time Stock Check ---
            const stockChecks = await Promise.all(
                cartItems.map(async item => {
                    try {
                        const rec = await pb.collection('patisseries').getOne(item.id);
                        return { ...rec, _collection: 'patisseries' };
                    } catch (e1) {
                        try {
                            const rec2 = await pb.collection('produits_carte').getOne(item.id);
                            return { ...rec2, _collection: 'produits_carte' };
                        } catch (e2) {
                            console.error("Impossible de trouver l'article:", item.id);
                            return null;
                        }
                    }
                })
            );

            const outOfStockItems = [];
            for (let i = 0; i < cartItems.length; i++) {
                const item = cartItems[i];
                const dbItem = stockChecks[i];
                if (!dbItem) {
                    outOfStockItems.push(`${item.name} (Introuvable)`);
                    continue;
                }
                if (dbItem.stock < item.quantity) {
                    outOfStockItems.push(`${item.name} (Restant: ${dbItem.stock})`);
                }
            }

            if (outOfStockItems.length > 0) {
                alert(`Désolé, certains articles ne sont plus disponibles en quantité suffisante :\n${outOfStockItems.join('\n')}\nVeuillez ajuster votre panier.`);
                setLoading(false);
                return;
            }
            // -----------------------------

            // --- User creation logic ---
            if (!currentUser && formData.createAccount && formData.password) {
                if (formData.password !== formData.passwordConfirm) {
                    alert("Les mots de passe ne correspondent pas.");
                    setLoading(false);
                    return;
                }
                try {
                    await pb.collection('users').create({
                        email: formData.email,
                        password: formData.password,
                        passwordConfirm: formData.passwordConfirm,
                        name: formData.name,
                        phone: formData.phone,
                    });
                    // Login instantly
                    await pb.collection('users').authWithPassword(formData.email, formData.password);
                } catch (userErr) {
                    console.error("Account creation failed", userErr);
                    if (userErr.status === 400 && userErr.data?.data?.email?.code === 'validation_not_unique') {
                        setShowEmailErrorModal(true);
                        setLoading(false);
                        return;
                    } else if (userErr.status === 400 && userErr.data?.data?.password) {
                        alert(`Erreur de mot de passe : ${userErr.data.data.password.message}`);
                        setLoading(false);
                        return;
                    }
                }
            }

            const pickupDate = new Date(`${formData.date}T${formData.time}`);

            const orderData = {
                client_name: formData.name,
                client_email: formData.email,
                client_phone: formData.phone,
                pickup_date: pickupDate.toISOString(),
                items: cartItems.map(item => ({
                    id: item.id,
                    name: item.name,
                    quantity: item.quantity,
                    price: item.prix
                })),
                total: cartTotal,
                status: 'pending',
                notes: formData.notes || ""
            };

            await pb.collection('commandes').create(orderData);

            // --- Update User Phone in Profile if requested ---
            if (currentUser && formData.updatePhoneInProfile) {
                try {
                    await pb.collection('users').update(currentUser.id, {
                        phone: formData.phone
                    });
                } catch (err) {
                    console.error("Error updating phone in profile:", err);
                }
            }
            // -------------------------------------------------

            // --- Update Stocks ---
            try {
                const stockUpdates = cartItems.map((item, idx) => {
                    const dbItem = stockChecks[idx];
                    if (!dbItem) return Promise.resolve();

                    const newStock = dbItem.stock - item.quantity;
                    return pb.collection(dbItem._collection).update(item.id, {
                        stock: newStock,
                    });
                });
                await Promise.all(stockUpdates);
            } catch (stockErr) {
                console.error("Critical: Error updating stocks after order creation:", stockErr);
            }
            // --------------------

            clearCart();

            // Celebration!
            const duration = 3 * 1000;
            const animationEnd = Date.now() + duration;
            const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 40000 };

            const randomInRange = (min, max) => Math.random() * (max - min) + min;

            const interval = setInterval(function () {
                const timeLeft = animationEnd - Date.now();

                if (timeLeft <= 0) {
                    return clearInterval(interval);
                }

                const particleCount = 50 * (timeLeft / duration);
                // since particles fall down, start a bit higher than random
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } });
                confetti({ ...defaults, particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } });
            }, 250);

            setShowSuccessModal(true);
        } catch (err) {
            console.error("COMMANDE ERROR (detailed):", err.data);

            if (err.status === 400) {
                const details = err.data?.data;
                let errorMsg = "Certaines informations ne sont pas valides :";
                if (details) {
                    // Extract exact field errors to show the user
                    const fields = Object.entries(details).map(([key, val]) => {
                        return `\n- ${key}: ${val.message || 'Format invalide'}`;
                    }).join('');
                    errorMsg += fields;
                } else {
                    errorMsg = "Erreur de validation. Veuillez vérifier vos coordonnées.";
                }
                alert(errorMsg);
                console.log("Field errors:", details);
            } else {
                alert("Une erreur technique est survenue. Veuillez nous contacter directement.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="checkout-page">
            <div className="container">
                <h1 style={{ marginBottom: '30px', fontFamily: 'var(--font-heading)', color: 'var(--color-brown)' }}>Valider ma commande</h1>

                {!currentUser && (
                    <div className="login-prompt-banner" style={{ background: 'var(--color-cream)', border: '1px solid var(--color-gold-light)', borderRadius: '12px', padding: '20px 30px', marginBottom: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ background: 'white', padding: '15px', borderRadius: '50%', color: 'var(--color-gold)', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' }}>
                                <User size={28} />
                            </div>
                            <div>
                                <h3 style={{ margin: '0 0 5px 0', color: 'var(--color-brown)', fontFamily: 'var(--font-heading-small)', fontSize: '1.6rem' }}>Déjà client chez nous ?</h3>
                                <p style={{ margin: 0, color: '#666', fontSize: '0.95rem' }}>Connectez-vous pour préremplir vos coordonnées et gagner du temps.</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline"
                            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '10px' }}
                            onClick={() => document.querySelector('.user-icon-btn')?.click()}
                        >
                            Me connecter
                        </button>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="checkout-form">
                    <div className="checkout-grid">

                        {/* Left Column: Details */}
                        <div>
                            <div className="checkout-section">
                                <h2>1. Vos Coordonnées</h2>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Nom complet</label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Téléphone</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                </div>
                                {currentUser && (
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '20px', fontSize: '0.85rem', color: '#666' }}>
                                        <input
                                            type="checkbox"
                                            name="updatePhoneInProfile"
                                            checked={formData.updatePhoneInProfile}
                                            onChange={handleChange}
                                            style={{ width: 'auto', marginRight: '10px' }}
                                        />
                                        Mettre à jour mon numéro de téléphone dans mon profil
                                    </label>
                                )}
                                <div className="form-group">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        disabled={!!currentUser} // Lock email if logged in
                                    />
                                </div>
                                {!currentUser && (
                                    <div style={{ marginTop: '15px', background: 'var(--color-cream)', padding: '15px', borderRadius: '8px', border: '1px solid var(--color-gold-light)' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', margin: 0, fontWeight: 'normal', color: 'var(--color-brown)' }}>
                                            <input
                                                type="checkbox"
                                                name="createAccount"
                                                checked={formData.createAccount}
                                                onChange={handleChange}
                                                style={{ width: 'auto', marginRight: '10px' }}
                                            />
                                            Créer un compte pour mémoriser mes informations et suivre ma commande
                                        </label>

                                        {formData.createAccount && (
                                            <div className="form-row" style={{ marginTop: '15px', marginBottom: 0 }}>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label>Mot de passe</label>
                                                    <input
                                                        type="password"
                                                        name="password"
                                                        value={formData.password}
                                                        onChange={handleChange}
                                                        required={formData.createAccount}
                                                        placeholder="8 caractères minimum"
                                                    />
                                                </div>
                                                <div className="form-group" style={{ marginBottom: 0 }}>
                                                    <label>Confirmer</label>
                                                    <input
                                                        type="password"
                                                        name="passwordConfirm"
                                                        value={formData.passwordConfirm}
                                                        onChange={handleChange}
                                                        required={formData.createAccount}
                                                        placeholder="Répétez le mot de passe"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {formData.createAccount && formData.password && formData.passwordConfirm && formData.password !== formData.passwordConfirm && (
                                            <p style={{ color: '#d93025', fontSize: '0.85rem', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <span style={{ fontSize: '1.2rem' }}>•</span> Les mots de passe ne correspondent pas.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="checkout-section">
                                <h2>2. Retrait en Boutique</h2>
                                <p style={{ marginBottom: '20px', color: '#666' }}>
                                    Lieu de retrait : <strong>102 Rue Porta, 81000 Albi</strong>
                                </p>

                                <div className="pickup-selector-container">
                                    <div
                                        className={`pickup-option-card ${formData.date === nextAvailable?.dateStr && formData.time === nextAvailable?.time ? 'active' : ''}`}
                                        onClick={() => {
                                            if (nextAvailable) {
                                                setFormData(prev => ({ ...prev, date: nextAvailable.dateStr, time: nextAvailable.time }));
                                            }
                                        }}
                                    >
                                        <div className="pickup-card-icon">
                                            <Clock size={20} />
                                        </div>
                                        <div className="pickup-card-info">
                                            <span className="pickup-card-label">Au plus tôt</span>
                                            <span className="pickup-card-value">{formatDateLong(nextAvailable?.dateStr)} à {nextAvailable?.time}</span>
                                        </div>
                                        {formData.date === nextAvailable?.dateStr && formData.time === nextAvailable?.time && (
                                            <div className="pickup-check">✓</div>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className="btn-select-other"
                                        onClick={() => setShowPicker(!showPicker)}
                                    >
                                        <Calendar size={18} style={{ marginRight: '10px' }} />
                                        {formData.date !== nextAvailable?.dateStr || formData.time !== nextAvailable?.time ? (
                                            <span>Prévu le : <strong>{formatDateLong(formData.date)} à {formData.time}</strong></span>
                                        ) : (
                                            "Choisir un autre moment"
                                        )}
                                        <ChevronRight size={18} style={{ marginLeft: 'auto', transform: showPicker && !isMobile ? 'rotate(90deg)' : 'none', transition: 'transform 0.3s' }} />
                                    </button>
                                </div>

                                {showPicker && !isMobile && (
                                    <div className="inline-picker-container">
                                        {renderPickerContent()}
                                    </div>
                                )}

                                {showPicker && isMobile && (
                                    <div className="pickup-modal-overlay" onClick={() => setShowPicker(false)}>
                                        <div className="pickup-modal" onClick={e => e.stopPropagation()}>
                                            <div className="modal-header">
                                                <h3>Choisir une date et heure</h3>
                                                <button type="button" className="close-btn" onClick={() => setShowPicker(false)}><X size={20} /></button>
                                            </div>

                                            <div className="modal-content">
                                                {renderPickerContent()}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="form-group" style={{ marginTop: '30px' }}>
                                    <label>Notes / Demandes spéciales (Facultatif)</label>
                                    <textarea
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                        rows="3"
                                        placeholder="Message pour une carte cadeau, allergies..."
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Summary */}
                        <div>
                            <div className="order-summary">
                                <h2>Récapitulatif</h2>
                                {cartItems.map(item => (
                                    <div key={item.id} className="summary-item">
                                        <span>{item.quantity}x {item.name}</span>
                                        <span>{(item.prix * item.quantity).toFixed(2)} €</span>
                                    </div>
                                ))}

                                <div className="summary-total">
                                    <span>Total à régler</span>
                                    <span>{cartTotal.toFixed(2)} €</span>
                                </div>

                                <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '15px' }}>
                                    Paiement lors du retrait en boutique (CB ou Espèces).
                                </p>

                                <button
                                    type="submit"
                                    className="submit-order-btn"
                                    disabled={loading}
                                >
                                    {loading ? 'Validation...' : 'Confirmer la commande'}
                                </button>
                            </div>
                        </div>

                    </div>
                </form>

                {showEmailErrorModal && (
                    <div className="pickup-modal-overlay" style={{ zIndex: 30000 }}>
                        <div className="pickup-modal" style={{ maxWidth: '450px', textAlign: 'center', padding: '40px' }}>
                            <div style={{ background: 'var(--color-cream)', width: '70px', height: '70px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 25px', color: 'var(--color-gold)' }}>
                                <User size={35} />
                            </div>
                            <h3 style={{ fontFamily: 'var(--font-heading-small)', fontSize: '1.8rem', color: 'var(--color-brown)', marginBottom: '15px' }}>Compte déjà existant</h3>
                            <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '30px' }}>
                                Un compte existe déjà avec l'adresse <strong>{formData.email}</strong>.<br />
                                Souhaitez-vous vous connecter pour retrouver vos informations ?
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                <button
                                    type="button"
                                    className="submit-order-btn"
                                    style={{ margin: 0 }}
                                    onClick={() => {
                                        setShowEmailErrorModal(false);
                                        document.querySelector('.user-icon-btn')?.click();
                                    }}
                                >
                                    Me connecter
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline"
                                    style={{ width: '100%', padding: '15px' }}
                                    onClick={() => {
                                        setFormData(prev => ({ ...prev, createAccount: false, password: '', passwordConfirm: '' }));
                                        setShowEmailErrorModal(false);
                                    }}
                                >
                                    Continuer sans compte
                                </button>
                            </div>
                            <button
                                type="button"
                                style={{ background: 'none', border: 'none', color: '#999', marginTop: '20px', cursor: 'pointer', fontSize: '0.9rem', textDecoration: 'underline' }}
                                onClick={() => setShowEmailErrorModal(false)}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                )}

                {showSuccessModal && (
                    <div className="pickup-modal-overlay" style={{ zIndex: 35000 }}>
                        <div className="pickup-modal" style={{ maxWidth: '450px', textAlign: 'center', padding: '50px 40px' }}>
                            <div style={{ background: '#ecfdf5', width: '80px', height: '80px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 25px', color: '#10b981', animation: 'scaleUp 0.5s ease' }}>
                                <CheckCircle size={45} />
                            </div>
                            <h3 style={{ fontFamily: 'var(--font-heading-small)', fontSize: '2.2rem', color: 'var(--color-brown)', marginBottom: '15px' }}>Merci !</h3>
                            <h4 style={{ color: '#10b981', marginBottom: '20px', fontWeight: '600' }}>C'est validé !</h4>

                            <p style={{ color: '#666', lineHeight: '1.6', marginBottom: '30px', fontSize: '1.05rem' }}>
                                Votre commande a été envoyée avec succès.<br />
                                On vous attend le<br />
                                <strong style={{ color: 'var(--color-brown)' }}>{formatDateLong(formData.date)} à {formData.time}</strong>
                            </p>

                            <button
                                type="button"
                                className="submit-order-btn"
                                style={{ margin: 0, background: '#10b981' }}
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    navigate('/mon-compte');
                                }}
                            >
                                Voir ma commande
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Checkout;
